import React from 'react'

import { replaceKeys } from '../../utilities'
import useAccess, { PERMS } from '../../hooks/useAccess'
import { useAgent } from '../AgentProvider'

export const ReadStatusContext = React.createContext(null)

const ReadStatusProvider = ({ children }) => {
  const { currentUser } = useAgent()
  const hasPermission = useAccess()

  const [convos, setConvos] = React.useState({})
  const [wholeConvos, setWholeConvos] = React.useState({})

  // Checks if a session should be added to the stack based on your permissions
  const canViewSession = React.useCallback(
    session => {
      if (hasPermission(PERMS.ConversationsFull)) {
        return true
      }

      const { assigned_to } = session

      if (
        hasPermission(PERMS.ConversationsBasic) &&
        hasPermission(PERMS.ConversationsActive)
      ) {
        return assigned_to === currentUser()?.id || !assigned_to
      }

      if (hasPermission(PERMS.ConversationsBasic)) {
        return assigned_to === currentUser()?.id
      }

      return false
    },
    [hasPermission, currentUser]
  )

  // Save initially loaded sessions from api
  const saveSessionsFromStack = React.useCallback(
    convos => {
      const { sessions = [], users = [] } = convos
      const conversations = {}
      const wholeConversations = {}

      sessions.forEach(sess => {
        if (canViewSession(sess)) {
          conversations[sess.id] = {}
          sess.userIds.forEach(id => {
            conversations[sess.id][id] = new Date(
              sess.read_status.find(obj => obj.user_id === id)?.stamp ??
                sess.createdAt
            )
          })
          wholeConversations[sess.id] = {
            ...sess,
            unread: sess.unread,
            cleared: false
          }
        }
      })

      users.forEach(user => {
        conversations[user.id] = {
          [user.id]: new Date(user.dm_state.last_read_other ?? user.created_at),
          [currentUser()?.id]: new Date(
            user.dm_state.last_read ?? user.created_at
          )
        }
        wholeConversations[user.id] = {
          ...user,
          unread: user.dm_state.unread,
          cleared: false
        }
      })

      setConvos(convs => ({ ...convs, ...conversations }))
      setWholeConvos(convs => ({ ...convs, ...wholeConversations }))
    },
    [canViewSession, currentUser]
  )

  // Save sessions on initial load
  const saveSessions = React.useCallback(convos => {
    const conversations = {}
    const wholeConversations = {}

    Object.entries(convos).forEach(([key, val]) => {
      conversations[key] = {}
      val.users.forEach(id => {
        conversations[key][id] = new Date(
          val.read_status.find(obj => obj.user_id === id)?.stamp ??
            val.createdAt
        )
      })
      wholeConversations[key] = {
        ...val,
        unread: val.unread,
        cleared: false
      }
    })

    setConvos(convs => ({ ...convs, ...conversations }))
    setWholeConvos(convs => ({ ...convs, ...wholeConversations }))
  }, [])

  const handleUpdateRead = React.useCallback(msg => {
    const { read_user_id, session_id } = msg.data
    const id = session_id ?? read_user_id

    setConvos(convos => ({
      ...convos,
      [id]: {
        ...convos[id],
        [read_user_id]: new Date()
      }
    }))
  }, [])

  const handleOwnRead = React.useCallback(
    (user, session) => {
      const id = user ?? session

      if (!id) {
        return
      }

      setConvos(convos => ({
        ...convos,
        [id]: { ...convos[id], [currentUser()?.id]: new Date() }
      }))
    },
    [currentUser]
  )

  const propagateMerge = React.useCallback((from, to) => {
    setConvos(convos => replaceKeys(convos, from, to, true))
  }, [])

  return (
    <ReadStatusContext.Provider
      value={{
        saveSessionsFromStack,
        saveSessions,
        handleUpdateRead,
        handleOwnRead,
        convos,
        setConvos,
        propagateMerge,
        wholeConvos,
        setWholeConvos
      }}
    >
      {children}
    </ReadStatusContext.Provider>
  )
}

export default ReadStatusProvider

export const useReadStatus = () => React.useContext(ReadStatusContext)
